import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Grid,
  Stack,
  Box,
  List,
  ListItem,
  IconButton,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Divider,
  Skeleton,
  Link,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import PageLayout from '@shared/shell/Layout/PageLayout';
import AddAddressDialog from '../address/AddAddressDialog';
import EditSupplierDialog from '../edit/EditSupplierDialog';
import { useSupplier } from '../../hooks/useSupplier';
import SupplierLinkedProductsTable from './SupplierLinkedProductsTable';
import { DeleteDialog } from '@shared/ui/dialog/DeleteDialog';

const SupplierDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [removeAddressId, setRemoveAddressId] = useState<string | null>(null);
  const [removeAddressName, setRemoveAddressName] = useState<string>('');
  const {
    supplier,
    isLoading,
    error,
    deleteSupplier,
    isDeleting,
    removeAddress,
    isRemovingAddress,
  } = useSupplier(id ?? '');

  if (isLoading) {
    return (
      <PageLayout title="Supplier Details" backAction={() => navigate('/suppliers')}>
        <Stack direction="column" spacing={2} py={2}>
          <Stack direction="row" spacing={4} alignItems="center">
            <Skeleton variant="rounded" width={56} height={56} />
            <Skeleton variant="text" width={200} height={24} />
          </Stack>
        </Stack>
        <Stack gap={8} sx={{ flexGrow: 1, py: 0 }}>
          <Grid container spacing={12} sx={{ flexGrow: 1, pt: 2 }}>
            <Grid item xs={12} md={8}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 1 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack gap={2}>
                <Skeleton variant="text" height={32} width="60%" />
                <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </PageLayout>
    );
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleDelete = async () => {
    if (!supplier) return;

    handleMenuClose();
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!supplier) return;

    try {
      await deleteSupplier(supplier.id);
      navigate('/suppliers');
    } catch (error) {
      console.error('Failed to delete supplier:', error);
      // TODO: Show error toast
    }
  };



  const handleRemoveAddress = async (addressId: string) => {
    if (!supplier) return;

    try {
      await removeAddress({
        supplierId: supplier.id,
        addressId,
        currentSupplier: supplier
      });
      setRemoveAddressId(null);
      setRemoveAddressName('');
    } catch (error) {
      console.error('Failed to remove address:', error);
      // TODO: Show error toast
    }
  };

  if (error || !supplier) {
    return (
      <PageLayout title="Supplier Details" backAction={() => navigate('/suppliers')}>
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {error ? `Could not load supplier: ${(error as Error).message}` : 'Supplier not found'}
          </Typography>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={supplier.name} backAction={() => navigate('/suppliers')}>
      <Divider sx={{ my: 1 }} />

      <Stack gap={8} sx={{ flexGrow: 1, py: 0, mt: 2 }}>
        <Grid container spacing={12} sx={{ flexGrow: 1, pt: 2 }}>
          <Grid item xs={12} md={8}>
            <Stack gap={6}>
              <SupplierLinkedProductsTable
                supplierProducts={supplier.supplierProducts}
                supplier={supplier}
              />
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Stack gap={6} position="sticky" top={80} zIndex={1000}>
              <Stack direction="column" flex={1} spacing={2}>


                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Typography variant="subtitle2" color="text.primary">Supplier info</Typography>
                  <Box>
                    <IconButton onClick={handleMenuOpen} aria-label="Supplier actions">
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      anchorEl={menuAnchorEl}
                      open={Boolean(menuAnchorEl)}
                      onClose={handleMenuClose}
                      PaperProps={{
                        sx: { minWidth: 150 },
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          setIsEditDialogOpen(true);
                          handleMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <EditIcon fontSize="small" />
                        </ListItemIcon>
                        Edit supplier
                      </MenuItem>
                      <MenuItem onClick={handleDelete} disabled={isDeleting}>
                        <ListItemIcon>
                          <DeleteIcon fontSize="small" />
                        </ListItemIcon>
                        Delete supplier
                      </MenuItem>
                    </Menu>
                  </Box>
                </Stack>

                <Stack direction="column" spacing={2}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {supplier.name}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {supplier.email}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {supplier.phone || 'Not set'}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Website
                    </Typography>
                    {supplier.website ? (
                      <Link
                        href={
                          supplier.website.startsWith('http://') || supplier.website.startsWith('https://')
                            ? supplier.website
                            : `https://${supplier.website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        sx={{ wordBreak: 'break-all' }}
                      >
                        {supplier.website}
                      </Link>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not set
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Stack>

              <Divider />

              <Stack direction="column" spacing={2}>
                <Typography variant="subtitle2" color="text.primary">
                  Addresses
                </Typography>
                {supplier.addresses.length === 0 ? (
                  <Typography variant="subtitle2" color="text.secondary">
                    No addresses yet. Add one below.
                  </Typography>
                ) : (
                  <List disablePadding>
                    {supplier.addresses.map(address => (
                      <ListItem key={address.id} sx={{ px: 0, alignItems: 'flex-start' }}>
                        <Stack spacing={0.5} flex={1} minWidth={0}>
                          <Typography variant="body2">{address.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {address.address}, {address.city}
                            <br />
                            {address.state} {address.zipCode}, {address.country}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                          <Chip label={address.type} size="small" variant="outlined" />
                          {isRemovingAddress && removeAddressId === address.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setRemoveAddressId(address.id);
                                setRemoveAddressName(address.name);
                              }}
                              disabled={isRemovingAddress && removeAddressId === address.id}
                              aria-label={`Remove ${address.name}`}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </ListItem>
                    ))}
                  </List>
                )}
                <Button variant="text" color="primary" endIcon={<AddIcon />} onClick={() => setIsAddAddressOpen(true)}>
                  Add address
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Stack>

      {/* Dialogs */}
      <AddAddressDialog
        open={isAddAddressOpen}
        onClose={() => setIsAddAddressOpen(false)}
        supplierId={supplier.id}
        currentSupplier={supplier}
      />

      <EditSupplierDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        supplier={supplier}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={isDeleteDialogOpen}
        title="Delete Supplier"
        description="Are you sure you want to delete this supplier? This action cannot be undone and will remove all associated addresses and product links."
        cancelDelete={() => setIsDeleteDialogOpen(false)}
        confirmDelete={confirmDelete}
        isDeleting={isDeleting}
      />

      {/* Remove Address Confirmation Dialog */}
      <Dialog
        open={Boolean(removeAddressId)}
        onClose={() => {
          setRemoveAddressId(null);
          setRemoveAddressName('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Address</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove "{removeAddressName}" from this supplier? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRemoveAddressId(null);
              setRemoveAddressName('');
            }}
            disabled={isRemovingAddress}
          >
            Cancel
          </Button>
          <Button
            onClick={() => removeAddressId && handleRemoveAddress(removeAddressId)}
            color="error"
            variant="contained"
            disabled={isRemovingAddress}
            startIcon={isRemovingAddress ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isRemovingAddress ? 'Removing...' : 'Remove Address'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default SupplierDetail; 